import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  TrendingUp,
  Globe,
  BarChart3,
  GitBranch,
  Users,
  FileText,
  Calendar,
  XCircle,
} from 'lucide-react';
import { apiClient } from '../lib/api/config.js';
import { StorySynthesisPanel } from '../components/threads/StorySynthesisPanel.js';
import { format } from 'date-fns';

const TYPE_ICONS: Record<string, typeof GitBranch> = {
  entity: Building2,
  activity: TrendingUp,
  sector: Globe,
  macro: BarChart3,
  shared_event: Users,
};

const TYPE_COLORS: Record<string, string> = {
  entity: 'bg-primary-100 text-primary-700',
  activity: 'bg-warning-100 text-warning-700',
  sector: 'bg-success-100 text-success-700',
  macro: 'bg-purple-100 text-purple-700',
  shared_event: 'bg-red-100 text-red-700',
};

const STATUS_COLORS: Record<string, string> = {
  detected: 'bg-blue-100 text-blue-700',
  candidate: 'bg-yellow-100 text-yellow-700',
  queued: 'bg-orange-100 text-orange-700',
  generating: 'bg-purple-100 text-purple-700',
  generated: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  dismissed: 'bg-grey-100 text-grey-500',
};

async function fetchThreadDetail(threadId: string) {
  const response = await apiClient.get(`/admin/threads/${threadId}`);
  return response.data;
}

export function ThreadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: thread, isLoading } = useQuery({
    queryKey: ['thread-detail', id],
    queryFn: () => fetchThreadDetail(id!),
    enabled: !!id,
  });

  const dismissMutation = useMutation({
    mutationFn: () => apiClient.post(`/admin/threads/${id}/dismiss`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['thread-detail', id] }),
  });

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center text-grey-500 py-16">Loading thread...</div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center text-grey-500 py-16">Thread not found</div>
      </div>
    );
  }

  const Icon = TYPE_ICONS[thread.thread_type] || GitBranch;
  const members = thread.members || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Back link */}
      <Link
        to="/intelligence"
        className="inline-flex items-center text-sm text-grey-500 hover:text-grey-700"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Story Threads
      </Link>

      {/* Header */}
      <div className="bg-surface rounded-xl border border-grey-200 p-8 shadow-md">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${TYPE_COLORS[thread.thread_type] || 'bg-grey-100'}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-grey-900">{thread.display_name}</h1>
              <div className="flex items-center gap-3 mt-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${TYPE_COLORS[thread.thread_type] || 'bg-grey-100'}`}>
                  {thread.thread_type}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[thread.status] || 'bg-grey-100'}`}>
                  {thread.status}
                </span>
                <span className="text-sm text-grey-500">
                  {members.length} member{members.length !== 1 ? 's' : ''}
                </span>
                <span className="text-sm text-grey-400">
                  Detected {format(new Date(thread.detected_at), 'MMM d, yyyy HH:mm')}
                </span>
              </div>
            </div>
          </div>

          {thread.status === 'detected' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => dismissMutation.mutate()}
                disabled={dismissMutation.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-grey-300 text-grey-600 hover:text-danger-600 hover:border-danger-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                <XCircle className="h-4 w-4" />
                Dismiss
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Story synthesis (M1, 2026-10-06) */}
      <div className="bg-surface rounded-xl border border-grey-200 p-8 shadow-md">
        <h2 className="text-xl font-bold text-grey-900 mb-4">Story synthesis</h2>
        <StorySynthesisPanel
          threadId={id!}
          threadType={thread.thread_type}
          status={thread.status}
          metadata={thread.thread_metadata}
        />
      </div>

      {/* Timeline */}
      <div className="bg-surface rounded-xl border border-grey-200 p-8 shadow-md">
        <h2 className="text-xl font-bold text-grey-900 mb-6">Constituent Announcements</h2>

        {members.length === 0 ? (
          <p className="text-grey-500">No members in this thread</p>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-grey-200" />

            <div className="space-y-6">
              {members.map((member: any, idx: number) => (
                <div key={member.member_id} className="relative pl-12">
                  {/* Timeline dot */}
                  <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-white ${
                    member.article_id ? 'bg-primary-500' : 'bg-grey-400'
                  }`} style={{ top: '6px' }} />

                  <div className="border border-grey-200 rounded-lg p-4 hover:border-grey-300 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-grey-900">
                          {member.announcement_title || 'Untitled Announcement'}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-sm text-grey-500">
                            {member.company_code} — {member.company_name}
                          </span>
                          {member.announcement_date && (
                            <span className="inline-flex items-center gap-1 text-xs text-grey-400">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(member.announcement_date), 'MMM d, yyyy')}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-grey-400 font-medium">#{member.position}</span>
                    </div>

                    {member.article_id && (
                      <Link
                        to={`/articles/${member.article_id}`}
                        className="inline-flex items-center gap-1.5 mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {member.article_title || 'View Article'}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
